import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Button from '@/components/primitives/Button.jsx';
import Input from '@/components/primitives/Input.jsx';
import Skeleton from '@/components/primitives/Skeleton.jsx';
import Textarea from '@/components/primitives/Textarea.jsx';
import BackLink from '@/components/patterns/BackLink.jsx';
import Dialog from '@/components/patterns/Dialog.jsx';
import buildSkillDocument from '@/lib/knowledge/buildSkillDocument.js';
import parseSkillDocument from '@/lib/knowledge/parseSkillDocument.js';
import pickFreeSlug from '@/lib/knowledge/pickFreeSlug.js';
import toKnowledgeSlug from '@/lib/knowledge/toKnowledgeSlug.js';
import toastManager from '@/lib/toast/toastManager.js';
import { putKnowledgeFile } from '@/redux/requests/knowledgeFilesRequests.js';
import { getSkill, getSkills, removeSkill } from '@/redux/requests/skillsRequests.js';
import { availableKnowledgeSkillItems } from '@/redux/selectors/pieveSelectors/sidebarSkillSelectors.js';

const EMPTY_DRAFT = { title: '', description: '', body: '', otherLines: ['kind: skill'], path: null };

/**
 * Writes or edits one skill: its name, when Pieve should use it, and the instructions. A new skill is
 * saved under a file name made from its name; saving again keeps that file and any settings in it.
 */
const SkillEditorPage = ({ skillId }) => {
  const navigate = useNavigate();
  const skills = useSelector(availableKnowledgeSkillItems);
  const isNew = skillId === 'new';
  const [draft, setDraft] = useState(isNew ? EMPTY_DRAFT : null);
  const [loadError, setLoadError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (isNew) {
      return;
    }

    getSkill({ nodeId: skillId }).then((res) => {
      const row = Array.isArray(res) ? res[0] : null;

      if (!row) {
        setLoadError(res?.message || 'This skill could not be loaded.');
        return;
      }

      setDraft({ ...parseSkillDocument(row.content), path: row.path });
    });
  }, [isNew, skillId]);

  const update = (key) => (event) => setDraft((current) => ({ ...current, [key]: event.target.value }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaveError('');
    setIsSaving(true);
    const takenSlugs = skills.map((skill) => skill.meta?.slug).filter(Boolean);
    const path = draft.path || `skills/global/${pickFreeSlug({ slug: toKnowledgeSlug(draft.title), takenSlugs })}/SKILL.md`;
    const res = await putKnowledgeFile({ path, content: buildSkillDocument(draft) });
    setIsSaving(false);

    if (res?.success === false) {
      setSaveError(res.message || 'The skill could not be saved.');
      return;
    }

    await getSkills();
    toastManager.add({ type: 'success', title: isNew ? 'Skill created' : 'Skill saved', description: isNew ? 'Pieve follows it from your next conversation.' : undefined });

    if (isNew) {
      navigate(`/skills/${res[0].id}`, { replace: true });
      return;
    }

    setDraft((current) => ({ ...current, path }));
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    const res = await removeSkill({ skillId });
    setIsDeleting(false);

    if (res?.success === false) {
      setSaveError(res.message || 'The skill could not be deleted.');
      setIsConfirmingDelete(false);
      return;
    }

    toastManager.add({ type: 'success', title: 'Skill deleted' });
    navigate('/skills');
  };

  if (loadError) {
    return (
      <div className="comp-page">
        <BackLink to="/skills">Skills</BackLink>
        <p className="skills-error">{loadError}</p>
      </div>
    );
  }

  return (
    <div className="comp-page skills-editor">
      <BackLink to="/skills">Skills</BackLink>
      <h1 className="skills-editor__title">{isNew ? 'New skill' : draft?.title || 'Skill'}</h1>
      {!draft ? (
        <Skeleton shape="block" height={320} />
      ) : (
        <form className="skills-editor__form" onSubmit={handleSubmit}>
          <Input label="Name" value={draft.title} onChange={update('title')} placeholder="Weekly sales summary" required />
          <Input label="When Pieve uses it" hint="One sentence. Pieve reads this to decide when the skill applies." value={draft.description} onChange={update('description')} placeholder="When I ask for the weekly sales numbers" />
          <Textarea label="Instructions" hint="Write the steps as you would explain them to a new colleague: what to check, what to produce, what to avoid." rows={14} value={draft.body} onChange={update('body')} />
          {saveError && (
            <p className="skills-error" role="alert">
              {saveError}
            </p>
          )}
          <div className="skills-editor__actions">
            <Button type="submit" variant="primary" loading={isSaving} disabled={!draft.title.trim() || !draft.body.trim()}>
              {isNew ? 'Create skill' : 'Save skill'}
            </Button>
            {!isNew && (
              <Button variant="ghost" onClick={() => setIsConfirmingDelete(true)}>
                Delete
              </Button>
            )}
          </div>
        </form>
      )}
      <Dialog
        open={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
        title={`Delete ${draft?.title || 'this skill'}?`}
        description="Pieve stops following it. Conversations that already used it keep their history."
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsConfirmingDelete(false)}>
              Cancel
            </Button>
            <Button variant="danger" loading={isDeleting} onClick={handleDelete}>
              Delete skill
            </Button>
          </>
        }
      />
    </div>
  );
};

export default SkillEditorPage;
